import React, { useEffect, useState } from 'react';
import { XIcon, StarIcon, ImagePlusIcon, PlusIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { ReviewTagChip } from './ReviewTagChip';
import { DisclosureQuestion } from '../Disclosure/DisclosureQuestion';
import { disclosuresFromAnswers, type DisclosureAnswerId, type DisclosureId, type DisclosureSubjectType } from '../../data/disclosures';
export type ReviewSubjectType = 'book' | 'merch' | 'bookstore' | 'audiobook' | 'box';
export interface ReviewSubject {
  id: string | number;
  type: ReviewSubjectType;
  title: string;
  image?: string;
  subtitle?: string; // author, creator, etc.
}
export interface ReviewDraft {
  id?: number;
  subject: ReviewSubject;
  rating: number;
  review: string;
  date: string;
  // Book specific
  moodTags?: string[];
  pacingTags?: string[];
  contentWarnings?: string[];
  favoriteQuote?: string;
  // Category ratings
  categoryRatings: Record<string, number>;
  /** Raw answers to the disclosure question, kept so the form can be re-edited. */
  disclosureAnswers?: DisclosureAnswerId[];
  /** Public disclosure badges derived from those answers. */
  disclosures?: DisclosureId[];
}

/** Maps the modal's subject types onto the disclosure taxonomy's subject types. */
const toDisclosureSubject = (type: ReviewSubjectType): DisclosureSubjectType => {
  switch (type) {
    case 'book':
      return 'book';
    case 'audiobook':
      return 'audiobook';
    case 'merch':
    case 'box':
      return 'product';
    case 'bookstore':
      return 'service';
    default:
      return 'product';
  }
};
interface WriteReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  subject?: ReviewSubject;
  existingReview?: ReviewDraft;
  onSave: (review: ReviewDraft) => void;
  zIndex?: number;
}
// Suggested tag vocabularies — curated starter lists. Users can also add their own.
const SUGGESTED_MOODS = ['Tense', 'Mysterious', 'Adventurous', 'Emotional', 'Dark', 'Lighthearted', 'Hopeful', 'Melancholic', 'Inspiring', 'Funny', 'Thought-provoking', 'Romantic', 'Reflective', 'Challenging', 'Cozy'];
const SUGGESTED_PACING = ['Fast-paced', 'Medium-paced', 'Slow-paced', 'Slow burn', 'Page-turner', 'Builds slowly', 'Quick read'];
const SUGGESTED_WARNINGS = ['Violence', 'Death', 'Grief', 'Sexual content', 'Trauma', 'Abuse', 'Substance use', 'Mental illness', 'Self-harm', 'Body horror', 'Animal harm', 'Gore'];
const getCategoriesForType = (type: ReviewSubjectType) => {
  switch (type) {
    case 'book':
    case 'audiobook':
      return ['Writing', 'Plot', 'Pacing', 'Characters', 'Enjoyment'];
    case 'merch':
    case 'box':
      return ['Quality', 'Value', 'Shipping', 'Design'];
    case 'bookstore':
      return ['Selection', 'Atmosphere', 'Service', 'Pricing'];
    default:
      return ['Overall'];
  }
};
export const WriteReviewModal: React.FC<WriteReviewModalProps> = ({
  isOpen,
  onClose,
  subject,
  existingReview,
  onSave,
  zIndex = 50
}) => {
  const dk = useDarkClasses();
  // Form State
  const [selectedType, setSelectedType] = useState<ReviewSubjectType>(subject?.type || existingReview?.subject.type || 'book');
  const [title, setTitle] = useState(subject?.title || existingReview?.subject.title || '');
  const [subtitle, setSubtitle] = useState(subject?.subtitle || existingReview?.subject.subtitle || '');
  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewText, setReviewText] = useState(existingReview?.review || '');
  const [categoryRatings, setCategoryRatings] = useState<Record<string, number>>(existingReview?.categoryRatings || {});
  const [quote, setQuote] = useState(existingReview?.favoriteQuote || '');
  const [moodTags, setMoodTags] = useState<string[]>(existingReview?.moodTags || []);
  const [pacingTags, setPacingTags] = useState<string[]>(existingReview?.pacingTags || []);
  const [contentWarnings, setContentWarnings] = useState<string[]>(existingReview?.contentWarnings || []);
  const [customMood, setCustomMood] = useState('');
  const [customPacing, setCustomPacing] = useState('');
  const [customWarning, setCustomWarning] = useState('');
  const [disclosureAnswers, setDisclosureAnswers] = useState<DisclosureAnswerId[]>(existingReview?.disclosureAnswers || []);
  // Reset form when opened with new data
  useEffect(() => {
    if (isOpen) {
      setSelectedType(subject?.type || existingReview?.subject.type || 'book');
      setTitle(subject?.title || existingReview?.subject.title || '');
      setSubtitle(subject?.subtitle || existingReview?.subject.subtitle || '');
      setRating(existingReview?.rating || 0);
      setReviewText(existingReview?.review || '');
      setCategoryRatings(existingReview?.categoryRatings || {});
      setQuote(existingReview?.favoriteQuote || '');
      setMoodTags(existingReview?.moodTags || []);
      setPacingTags(existingReview?.pacingTags || []);
      setContentWarnings(existingReview?.contentWarnings || []);
      setCustomMood('');
      setCustomPacing('');
      setCustomWarning('');
      setDisclosureAnswers(existingReview?.disclosureAnswers || []);
    }
  }, [isOpen, subject, existingReview]);
  if (!isOpen) return null;
  const categories = getCategoriesForType(selectedType);
  const handleCategoryRating = (cat: string, val: number) => {
    setCategoryRatings(prev => ({
      ...prev,
      [cat]: val
    }));
  };
  const toggleTag = (list: string[], setList: (v: string[]) => void, tag: string) => {
    if (list.includes(tag)) {
      setList(list.filter(t => t !== tag));
    } else {
      setList([...list, tag]);
    }
  };
  const addCustomTag = (list: string[], setList: (v: string[]) => void, custom: string, setCustom: (v: string) => void) => {
    const trimmed = custom.trim();
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setCustom('');
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalSubject: ReviewSubject = subject || existingReview?.subject || {
      id: Date.now().toString(),
      type: selectedType,
      title: title || 'Untitled',
      subtitle: subtitle
    };
    const draft: ReviewDraft = {
      id: existingReview?.id || Date.now(),
      subject: finalSubject,
      rating,
      review: reviewText,
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      categoryRatings,
      favoriteQuote: quote,
      moodTags,
      pacingTags,
      contentWarnings,
      disclosureAnswers,
      // Badges are DERIVED, never hand-entered, so the public label always
      // matches what the reviewer actually declared.
      disclosures: disclosuresFromAnswers(disclosureAnswers, toDisclosureSubject(finalSubject.type))
    };
    onSave(draft);
    onClose();
  };
  const isFixedSubject = !!subject || !!existingReview;
  const showNarrativeTags = selectedType === 'book' || selectedType === 'audiobook';
  // Reusable tag picker section
  const renderTagSection = (label: string, helper: string, variant: 'mood' | 'pacing' | 'warning', suggestions: string[], selected: string[], setSelected: (v: string[]) => void, custom: string, setCustom: (v: string) => void) => <div data-id="element-3303" data-mp="28p7i7707qkqi">
      <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-3304" data-mp="28oxs7jf64dkt">
        {label}
      </label>
      <p className={`text-xs mb-2.5 ${dk.textMuted}`} data-id="element-3305" data-mp="1upy2s9jtzunl">{helper}</p>

      {/* Selected tags row */}
      {selected.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-3306" data-mp="3p25sn8haplci">
          {selected.map(tag => <ReviewTagChip key={tag} label={tag} variant={variant} onRemove={t => setSelected(selected.filter(x => x !== t))} data-id="element-3307" data-mp="3u0z8v8nyvqgd" />)}
        </div>}

      {/* Suggested chips — toggleable */}
      <div className="flex flex-wrap gap-1.5 mb-2.5" data-id="element-3308" data-mp="11j0ixocvk3jl">
        {suggestions.map(tag => {
        const isActive = selected.includes(tag);
        return <button key={tag} type="button" onClick={() => toggleTag(selected, setSelected, tag)} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${isActive ? 'bg-indigo-600 border-indigo-600 text-white' : `${dk.card} ${dk.border} ${dk.textSecondary} hover:${dk.bgMuted}`}`} data-id="element-3309" data-mp="snuaehm7z7dd">
              {tag}
            </button>;
      })}
      </div>

      {/* Free-text input */}
      <div className="flex gap-2" data-id="element-3310" data-mp="285w2bv6ec09w">
        <input type="text" value={custom} onChange={e => setCustom(e.target.value)} onKeyDown={e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          addCustomTag(selected, setSelected, custom, setCustom);
        }
      }} placeholder="Add your own..." className={`flex-1 rounded-lg border px-3 py-1.5 text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none ${dk.input}`} data-id="element-3311" data-mp="2842r02ku8rvk" />
        <button type="button" onClick={() => addCustomTag(selected, setSelected, custom, setCustom)} disabled={!custom.trim()} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${dk.card} ${dk.border} ${dk.textSecondary} hover:${dk.bgMuted}`} data-id="element-3312" data-mp="3m4qpv292u0y">
          <PlusIcon className="h-3.5 w-3.5" data-id="element-3313" data-mp="3h2uaqmd0tpmw" />
        </button>
      </div>
    </div>;
  return <div className={`fixed inset-0 flex items-center justify-center p-4 sm:p-6`} style={{
    zIndex
  }} data-id="element-3314" data-mp="rdii9yskulkv">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-id="element-3315" data-mp="waims59s3g9v" />

      <div className={`relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border ${dk.card} ${dk.border}`} data-id="element-3316" data-mp="22h0oibnay9vg">
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b ${dk.border}`} data-id="element-3317" data-mp="2c8551fsmm2se">
          <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-3318" data-mp="2h4vjjyos8qbp">
            {existingReview ? 'Edit Review' : 'Write a Review'}
          </h2>
          <button onClick={onClose} className={`p-2 rounded-full transition-colors hover:${dk.bgMuted} ${dk.textMuted}`} data-id="element-3319" data-mp="1ohhsuibogr01">
            <XIcon className="h-5 w-5" data-id="element-3320" data-mp="1tf15jdsq8vpv" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-6" data-id="element-3321" data-mp="1xjr0n0fka69q">
          <form id="review-form" onSubmit={handleSubmit} className="space-y-6" data-id="element-3322" data-mp="34waqy5vf1xtt">
            {/* Subject Selection (if not pre-filled) */}
            {!isFixedSubject && <div className="space-y-4" data-id="element-3323" data-mp="x9zpc9i2ic8u">
                <div data-id="element-3324" data-mp="x8y5pgmzkhfi">
                  <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-3325" data-mp="x97u4zsdugj3">
                    What are you reviewing?
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-3326" data-mp="ofuuzg3q6iok">
                    {(['book', 'merch', 'bookstore', 'audiobook', 'box'] as ReviewSubjectType[]).map(type => <button key={type} type="button" onClick={() => {
                  setSelectedType(type);
                  setCategoryRatings({});
                }} className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors border ${selectedType === type ? 'bg-indigo-600 border-indigo-600 text-white' : `${dk.card} ${dk.border} ${dk.textSecondary} hover:${dk.bgMuted}`}`} data-id="element-3327" data-mp="5i1sgex0karp">
                        {type}
                      </button>)}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3328" data-mp="2a0je6d4gnf53">
                  <div data-id="element-3329" data-mp="29you1110jr22">
                    <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-3330" data-mp="2xqn5pm4djofq">
                      Title / Name
                    </label>
                    <input required type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={`e.g. The Silent Echo`} className={`w-full rounded-lg border px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none ${dk.input}`} data-id="element-3331" data-mp="2nsg204z3j2vo" />
                  </div>
                  <div data-id="element-3332" data-mp="19r9wsc65pn8y">
                    <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-3333" data-mp="19qrtwwozoas2">
                      Author / Creator (Optional)
                    </label>
                    <input type="text" value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="e.g. Elena Richards" className={`w-full rounded-lg border px-4 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:outline-none ${dk.input}`} data-id="element-3334" data-mp="1iqx053ig534n" />
                  </div>
                </div>
              </div>}

            {/* Fixed Subject Display */}
            {isFixedSubject && <div className={`flex items-center gap-4 p-4 rounded-xl border ${dk.bgMuted}/30 ${dk.border}`} data-id="element-3335" data-mp="7dcecmbpip0f">
                {subject?.image || existingReview?.subject.image ? <img src={subject?.image || existingReview?.subject.image} alt="Subject" className="w-12 h-16 object-cover rounded shadow-sm" data-id="element-3336" data-mp="g2t2pya8oakz" /> : <div className={`w-12 h-16 rounded flex items-center justify-center ${dk.bgMuted}`} data-id="element-3337" data-mp="py372so40u1x">
                    <ImagePlusIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-3338" data-mp="yt11k46woevd" />
                  </div>}
                <div data-id="element-3339" data-mp="13q180g7o3jn0">
                  <h3 className={`font-bold ${dk.textHeading}`} data-id="element-3340" data-mp="13phs151kv5bm">
                    {subject?.title || existingReview?.subject.title}
                  </h3>
                  <p className={`text-sm ${dk.textSecondary}`} data-id="element-3341" data-mp="1dps6eszcmisd">
                    {subject?.subtitle || existingReview?.subject.subtitle}
                  </p>
                  <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full capitalize ${dk.bgMuted} ${dk.textSecondary}`} data-id="element-3342" data-mp="3ak4y8n61unnt">
                    {selectedType}
                  </span>
                </div>
              </div>}

            {/* Overall Rating */}
            <div data-id="element-3343" data-mp="1nsxezck6ca0b">
              <label className={`block text-sm font-medium mb-2 ${dk.textPrimary}`} data-id="element-3344" data-mp="1nuibh7q1u30t">
                Overall Rating
              </label>
              <div className="flex gap-1" data-id="element-3345" data-mp="2fkd0f39zvqoy">
                {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onMouseEnter={() => setHoverRating(star)} onMouseLeave={() => setHoverRating(0)} onClick={() => setRating(star)} className="p-1 transition-transform hover:scale-110 focus:outline-none" data-id="element-3346" data-mp="2bj1fvc5z9vnm">
                    <StarIcon className={`h-8 w-8 transition-colors ${(hoverRating || rating) >= star ? 'text-amber-400 fill-amber-400' : `${dk.textMuted} opacity-30`}`} data-id="element-3347" data-mp="v2ouvf62vfj3" />
                  </button>)}
              </div>
            </div>

            {/* Review Text */}
            <div data-id="element-3348" data-mp="imfos1mm3qfl">
              <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-3349" data-mp="imyvem6taseb">
                Your Review
              </label>
              <textarea required rows={5} value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="What did you think? Share your detailed thoughts..." className={`w-full rounded-lg border px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-none ${dk.input}`} data-id="element-3350" data-mp="8wcn7d1fd45n" />
            </div>

            {/* Commercial disclosure — captured structurally so reviewers never
                have to remember to type #gifted or #ad. */}
            <DisclosureQuestion value={disclosureAnswers} onChange={setDisclosureAnswers} subjectType={toDisclosureSubject(selectedType)} data-id="element-3351" data-mp="3r17m1oprx93u" />

            {/* Favorite Quote (Books only) */}
            {(selectedType === 'book' || selectedType === 'audiobook') && <div data-id="element-3352" data-mp="3cfa5ny7xn16u">
                <label className={`block text-sm font-medium mb-1.5 ${dk.textPrimary}`} data-id="element-3353" data-mp="3ceqztfvk4396">
                  Favorite Quote (Optional)
                </label>
                <textarea rows={2} value={quote} onChange={e => setQuote(e.target.value)} placeholder="Did a specific line stand out to you?" className={`w-full rounded-lg border px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:outline-none resize-none ${dk.input}`} data-id="element-3354" data-mp="3lg8a2hma9apy" />
              </div>}

            {/* Narrative Tags — only for book/audiobook */}
            {showNarrativeTags && <div className="space-y-5 pt-2 border-t border-dashed ${dk.border}" data-id="element-3355" data-mp="1fe14wpapqpwv">
                {renderTagSection('Mood', 'How did this book feel? Tap to add or remove.', 'mood', SUGGESTED_MOODS, moodTags, setMoodTags, customMood, setCustomMood)}
                {renderTagSection('Pacing', 'How did the story move?', 'pacing', SUGGESTED_PACING, pacingTags, setPacingTags, customPacing, setCustomPacing)}
                {renderTagSection('Content Warnings', 'Help future readers know what to expect.', 'warning', SUGGESTED_WARNINGS, contentWarnings, setContentWarnings, customWarning, setCustomWarning)}
              </div>}

            {/* Category Ratings */}
            <div data-id="element-3356" data-mp="5dp02pcekhjx">
              <label className={`block text-sm font-medium mb-3 ${dk.textPrimary}`} data-id="element-3357" data-mp="1do8s447gbvg">
                Rate Specific Aspects
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-id="element-3358" data-mp="3smk6maunp2do">
                {categories.map(cat => <div key={cat} className="flex items-center justify-between" data-id="element-3359" data-mp="3itwc5mr5fai4">
                    <span className={`text-sm ${dk.textSecondary}`} data-id="element-3360" data-mp="3dvbbjm7ziot5">{cat}</span>
                    <div className="flex gap-1" data-id="element-3361" data-mp="3dt8nh7qe75r2">
                      {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => handleCategoryRating(cat, star)} className="p-0.5 focus:outline-none" data-id="element-3362" data-mp="39rwtkq0hjyd2">
                          <StarIcon className={`h-5 w-5 ${(categoryRatings[cat] || 0) >= star ? 'text-amber-400 fill-amber-400' : `${dk.textMuted} opacity-30`}`} data-id="element-3363" data-mp="2ymp21byueuqk" />
                        </button>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-end gap-3 p-5 border-t ${dk.border} bg-black/5`} data-id="element-3364" data-mp="2fq6139y70d53">
          <button type="button" onClick={onClose} className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${dk.textPrimary} hover:${dk.bgMuted}`} data-id="element-3365" data-mp="25y9ybll5vyuj">
            Cancel
          </button>
          <button type="submit" form="review-form" disabled={!rating || !reviewText || !isFixedSubject && !title} className="px-6 py-2.5 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-3366" data-mp="1vzu9xqdir3oq">
            Publish Review
          </button>
        </div>
      </div>
    </div>;
};